// ProteoRisk: disease-risk scoring from blood proteins. One Olink Explore HT
// workbook plus age, sex and optional BMI goes through the XOmics
// olink-proteomics pipeline on the common webserver lifecycle
// (${WEBSERVER_API_ORIGIN}/api/proteorisk).
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { NavLink, Navigate, Route, Routes, useLocation } from 'react-router-dom';

import './proteorisk.css';
import './proteorisk-light.css';
import { ensureProteoRiskCookie } from './WebserverUser';
import { isTerminal, loadJobs, loadProteoRiskContract } from './modules/api';
import ApiGuide from './modules/ApiGuide';
import { ProteoRiskContext } from './modules/context';
import Info from './modules/Info';
import Analyze from './modules/Analyze';
import JobPage from './modules/JobPage';
import ExampleReport from './modules/ExampleReport';
import Help from './modules/Help';
import Settings from './modules/Settings';
import baseLogoWhite from './assets/phenome-mark-white.svg';
import baseLogoBlack from './assets/phenome-mark-black.svg';

const THEME_STORAGE_KEY = 'proteorisk-theme-preference';
const JOBS_POLL_MS = 5000;

function storedTheme() {
  if (typeof window === 'undefined') return 'dark';
  const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
  if (stored === 'light') return 'light';
  if (stored === 'system') {
    return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'dark';
}

function applyRootTheme(theme) {
  const root = document.documentElement;
  root.classList.remove('proteorisk-theme-light', 'proteorisk-theme-dark');
  root.classList.add(theme === 'light' ? 'proteorisk-theme-light' : 'proteorisk-theme-dark');
}

// Apply the theme before the first paint, like the other webservers.
if (typeof document !== 'undefined') applyRootTheme(storedTheme());

const TABS = [
  { to: '/proteorisk', label: 'Home', end: true, also: '/proteorisk/jobs/' },
  { to: '/proteorisk/info', label: 'Info', also: '/proteorisk/example' },
  { to: '/proteorisk/api', label: 'API' },
  { to: '/proteorisk/help', label: 'Help' },
  { to: '/proteorisk/settings', label: 'Settings' },
];

export default function ProteoRisk() {
  const location = useLocation();
  const [theme, setThemeState] = useState(storedTheme);
  const [cookieId, setCookieId] = useState('');
  const [credentialError, setCredentialError] = useState('');
  const [contract, setContract] = useState(null);
  const [contractError, setContractError] = useState('');
  const [jobs, setJobs] = useState([]);
  const [jobsState, setJobsState] = useState({ loading: true, error: '' });

  const setTheme = useCallback((next) => {
    setThemeState(next);
    window.localStorage.setItem(THEME_STORAGE_KEY, next);
    applyRootTheme(next);
  }, []);

  useEffect(() => {
    ensureProteoRiskCookie()
      .then((cookie) => setCookieId(cookie.id))
      .catch((error) => {
        setCredentialError(error.message || 'This browser could not be registered.');
        setJobsState({ loading: false, error: 'This browser could not be registered with the analysis service.' });
      });
  }, []);

  useEffect(() => {
    if (!cookieId) return;
    loadProteoRiskContract(cookieId)
      .then((loaded) => {
        setContract(loaded);
        setContractError('');
      })
      .catch((error) => setContractError(error.message || 'The ProteoRisk pipeline could not be loaded.'));
  }, [cookieId]);

  const refreshJobs = useCallback(async (showLoading = true) => {
    if (!cookieId) return;
    if (showLoading) setJobsState((current) => ({ ...current, loading: true }));
    try {
      setJobs(await loadJobs(cookieId));
      setJobsState({ loading: false, error: '' });
    } catch (error) {
      setJobsState({ loading: false, error: error.message || 'Your analyses could not be loaded.' });
    }
  }, [cookieId]);

  useEffect(() => { refreshJobs(); }, [refreshJobs]);

  // Keep the list fresh while anything is still on its way.
  useEffect(() => {
    if (!jobs.some((job) => !isTerminal(job.status))) return undefined;
    const timer = setInterval(() => refreshJobs(false), JOBS_POLL_MS);
    return () => clearInterval(timer);
  }, [jobs, refreshJobs]);

  const context = useMemo(() => ({
    theme,
    setTheme,
    cookieId,
    contract,
    contractError: credentialError || contractError,
    jobs,
    jobsState,
    refreshJobs,
  }), [theme, setTheme, cookieId, contract, credentialError, contractError, jobs, jobsState, refreshJobs]);

  return (
    <ProteoRiskContext.Provider value={context}>
      <div className={`PROTEORISK-app-container ${theme === 'light' ? 'light-theme' : ''}`}>
        <header className="PROTEORISK-header">
          <div className="PROTEORISK-header-left">
            <a href="/" className="PROTEORISK-logo-container">
              <img src={theme === 'light' ? baseLogoBlack : baseLogoWhite} alt="Home" className="PROTEORISK-logo-img" />
            </a>
            <nav className="PROTEORISK-nav-tabs" aria-label="ProteoRisk">
              {TABS.map((tab) => (
                <NavLink
                  key={tab.to}
                  to={tab.to}
                  end={tab.end}
                  className={({ isActive }) => `PROTEORISK-nav-tab ${
                    isActive || (tab.also && location.pathname.startsWith(tab.also)) ? 'active' : ''}`}
                >
                  {tab.label}
                </NavLink>
              ))}
            </nav>
          </div>
        </header>

        <main className="PROTEORISK-content-area">
          <Routes>
            <Route index element={<Analyze />} />
            <Route path="info" element={<Info />} />
            <Route path="analyze" element={<Navigate to="/proteorisk" replace />} />
            <Route path="jobs/:uploadUuid" element={<JobPage />} />
            <Route path="example" element={<ExampleReport />} />
            <Route path="api" element={<ApiGuide />} />
            <Route path="help" element={<Help />} />
            <Route path="settings" element={<Settings />} />
            <Route path="*" element={<Navigate to="/proteorisk" replace />} />
          </Routes>
        </main>

        <div className="PROTEORISK-cookie-icon">
          <i className="fas fa-cookie-bite" aria-hidden="true" />
          <span className="PROTEORISK-tooltip">{`ID: ${cookieId || 'not registered'}`}</span>
        </div>
      </div>
    </ProteoRiskContext.Provider>
  );
}
